import { h } from 'preact'
import { useEffect, useState } from 'preact/compat'
import { useSelector } from 'react-redux'
import { useTranslations } from '../../../hooks/useTranslations'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import VisaLogo from '../../../assets/icons/Visa-Logo.svg'
import MastercardLogo from '../../../assets/icons/Mastercard-Logo.svg'
import MaestroLogo from '../../../assets/icons/Maestro-Logo.svg'
import AmericanExpressLogo from '../../../assets/icons/American-Express-Logo.svg'
import ApplePayLogo from '../../../assets/icons/ApplePay_logo.svg'
import PayPalLogo from '../../../assets/icons/PayPal_logo.svg'
import CardDebitTui from '../../../assets/icons/card-debit-tui.svg'
import TrustlyLogo from '../../../assets/icons/trustly-green-logo.svg'
import VippsLogo from '../../../assets/icons/logos-payment-vipps.svg'
import MobilePayLogo from '../../../assets/icons/mobile-pay-icon.svg'
import WalleyLogo from '../../../assets/icons/walley-logo.svg'
import BancontactLogo from '../../../assets/icons/Bancontact-Original-logo-RGB.svg'
import iDealLogo from '../../../assets/icons/ideal-wero.svg'
import DirectDebitLogo from '../../../assets/icons/Direct-debit-logo.svg'
import GooglePay from '../../../assets/icons/google-pay-mark_800.svg'
import SwishLogo from '../../../assets/icons/swish-icon.svg'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { isMobile } from '../../../helpers/common.js'
import { sprintf } from '../../../helpers/common'
import './styles.scss'

const paymentTypes = {
  ApplePay: 'ApplePay',
  PayPal: 'PayPal',
  Bancontact: 'Bancontact',
  Trustly: 'Trustly',
  Vipps: 'Vipps',
  Walley: 'Walley',
  MobilePay: 'MobilePay',
  Swish: 'Swish',
  IDeal: 'IDeal',
  DirectDebit: 'DirectDebit',
  GooglePay: 'GooglePay',
}

export const paymentsMethods = [
  {
    logo: VisaLogo,
    title: 'Visa',
    type: 'Visa',
  },
  {
    logo: MastercardLogo,
    title: 'Mastercard',
    type: 'MasterCard',
  },
  {
    logo: MaestroLogo,
    title: 'Maestro',
    type: 'Maestro',
  },
  {
    logo: AmericanExpressLogo,
    title: 'American',
    type: 'AmEx',
  },
  {
    logo: ApplePayLogo,
    title: 'Apple Pay',
    type: paymentTypes.ApplePay,
  },
  {
    logo: BancontactLogo,
    title: 'Bancontact',
    type: paymentTypes.Bancontact,
    className: 'bancontact-accepted-logo',
  },
  {
    logo: PayPalLogo,
    title: 'PayPal',
    type: paymentTypes.PayPal,
  },
  {
    logo: CardDebitTui,
    title: 'Tui debit card',
    type: 'GiftCard',
    className: 'tui-card',
  },
  {
    logo: TrustlyLogo,
    title: 'Trustly',
    type: paymentTypes.Trustly,
  },
  {
    logo: VippsLogo,
    title: 'Vipps',
    type: paymentTypes.Vipps,
  },
  {
    logo: WalleyLogo,
    title: 'Walley',
    type: paymentTypes.Walley,
  },
  {
    logo: MobilePayLogo,
    title: 'MobilePay',
    type: paymentTypes.MobilePay,
  },
  {
    logo: SwishLogo,
    title: 'Swish',
    type: paymentTypes.Swish,
  },
  {
    logo: iDealLogo,
    title: 'IDeal',
    type: paymentTypes.IDeal,
  },

  {
    logo: DirectDebitLogo,
    title: 'Direct Debit',
    type: paymentTypes.DirectDebit,
  },

  {
    logo: GooglePay,
    title: 'GooglePay',
    type: paymentTypes.GooglePay,
    className: 'googlepay-logo',
  },
]
const CARD_TYPE = ['MasterCard', 'GiftCard', 'AmEx', 'Visa', 'Maestro']

export default function PaymentAcceptedMethods() {
  const l10n = useTranslations()
  const { displayedPaymentMethods } = useSelector(pageStateSelector)
  const { prohibitedCardBrands } = useSelector(transactionDetailsSelector)

  const availablePaymentsMethods = paymentsMethods

  const [allowedPaymentsMethods, setAllowedPaymentsMethods] = useState(
    availablePaymentsMethods,
  )

  useEffect(() => {
    let paymentsMethodsToDisplay = availablePaymentsMethods

    const isCardPaymentEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.Card,
    )
    if (!isCardPaymentEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        el => !CARD_TYPE.includes(el.type),
      )
    }

    if (prohibitedCardBrands?.length) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        el => !prohibitedCardBrands?.includes(el.type),
      )
    }

    const isApplePayPaymentEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.ApplePay,
    )
    if (!isApplePayPaymentEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.ApplePay,
      )
    }

    const isPayPalPaymentEnabled =
      displayedPaymentMethods.includes(PAYMENT_METHOD_TYPES.PayPal) ||
      displayedPaymentMethods.includes(PAYMENT_METHOD_TYPES.PayPalCredit)

    if (!isPayPalPaymentEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.PayPal,
      )
    }

    const isBancontactEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.Bancontact,
    )

    if (!isBancontactEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.Bancontact,
      )
    }

    const isTrustlyEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.Trustly,
    )
    if (!isTrustlyEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.Trustly,
      )
    }

    const isVippsEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.Vipps,
    )
    if (!isVippsEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.Vipps,
      )
    }

    const isMobilePayEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.MobilePay,
    )
    if (!isMobilePayEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.MobilePay,
      )
    }

    const isSwishEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.Swish,
    )
    if (!isSwishEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.Swish,
      )
    }

    const isWalleyEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.Walley,
    )
    if (!isWalleyEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.Walley,
      )
    }

    const isIDealEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.IDeal,
    )

    if (!isIDealEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.IDeal,
      )
    }

    const isDirectDebitEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.DirectDebit,
    )

    if (!isDirectDebitEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.DirectDebit,
      )
    }

    const isGooglePayEnabled = displayedPaymentMethods.includes(
      PAYMENT_METHOD_TYPES.GooglePay,
    )

    if (!isGooglePayEnabled) {
      paymentsMethodsToDisplay = paymentsMethodsToDisplay.filter(
        method => method.type !== paymentTypes.GooglePay,
      )
    }

    setAllowedPaymentsMethods(paymentsMethodsToDisplay)
  }, [displayedPaymentMethods])

  let firstLogoLine = allowedPaymentsMethods.slice(0, 8)
  const secondLogoLine = allowedPaymentsMethods.slice(8)

  if (isMobile()) {
    firstLogoLine = allowedPaymentsMethods.filter(
      allowedPaymentMethod =>
        CARD_TYPE.includes(allowedPaymentMethod.type) ||
        allowedPaymentMethod.type === PAYMENT_METHOD_TYPES.ApplePay ||
        allowedPaymentMethod.type === PAYMENT_METHOD_TYPES.GooglePay,
    )
  }

  const remainingPaymentMethods =
    allowedPaymentsMethods.length - firstLogoLine.length

  return (
    <tui-payments-accepted-methods
      className="mfe-component allowed-payment-methods-info-header"
      data-testid="payments-accepted-methods"
    >
      <div className="allowed-payment-methods-info-header__divider">
        {l10n.t('We accept these methods of payment:')}
      </div>
      <div
        aria-label={l10n.t('Accepted payment methods')}
        className="allowed-payment-methods-info-header__logo"
      >
        {firstLogoLine.map(method => (
          <img
            className={`logo-image ${method.className || ''}`}
            src={method.logo}
            alt={method.title}
            key={method.title}
            data-testid={`${method.title}-icon`}
          />
        ))}
      </div>

      {isMobile() && !!secondLogoLine.length && (
        <span className="number-of-methods">
          {sprintf(
            '%s other payment methods are available.',
            remainingPaymentMethods,
          )}
        </span>
      )}
      {!!secondLogoLine.length && !isMobile() && (
        <div
          aria-label={l10n.t('Accepted payment methods')}
          className="allowed-payment-methods-info-header__logo second-logo-line"
        >
          {secondLogoLine.map(method => (
            <img
              className={`logo-image ${method.className || ''}`}
              src={method.logo}
              alt={method.title}
              key={method.title}
              data-testid={`${method.title}-icon`}
            />
          ))}
        </div>
      )}
    </tui-payments-accepted-methods>
  )
}
